import React, { useState, useMemo } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  PageLayoutWidgetDto,
  PublishedQuoteListItemDto,
} from "@lib/ShiOneClient";
import {
  Box,
  Button,
  CircularProgress,
  Link,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Alert,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ShoppingCart } from "@mui/icons-material";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import { SkeletonWrapper, WidgetWrapper } from "shared-ui";
import { useClaims } from "../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../auth/authorizationTypes";
import { getShiComBaseUrl } from "@lib/shiComBaseUrl";
import { AppendIfPrefixExists } from "../../../../modules/sitePaths";
import useGetQuotes from "../Hooks/useGetQuotes";
import { useAddQuoteToCart } from "../Hooks/useAddQuoteToCart";
import BuyAgainResultDialog from "@features/ecommerce/components/BuyAgainResultDialog";
import {
  formatQuoteSecondaryText,
  getQuoteExpirationInfo,
  filterAndSortQuotes,
} from "../Helpers/RecentQuoteWidgetHelpers";

function NoQuotesView() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={0.75}
      sx={{ py: 4, px: 2 }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <RequestQuoteOutlinedIcon sx={{ color: "action.active", fontSize: 20 }} />
        <Typography variant="body1" color="text.secondary">
          No Quotes Yet
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Request your account team to generate quotes for you or save items in your cart as a quote.
      </Typography>
    </Stack>
  );
}

function QuoteItem({
  quote,
  loading,
  onAddToCart,
  isTabularView,
  isLoading,
}: {
  quote: PublishedQuoteListItemDto;
  loading: boolean;
  onAddToCart: (quoteId: number) => void;
  isTabularView?: boolean;
  isLoading?: boolean;
}) {
  const theme = useTheme();
  const navigate = useNavigate();

  const secondaryTextParts = useMemo(
    () => formatQuoteSecondaryText(quote, isTabularView),
    [quote.quoteId, quote.contact, quote.itemCount, quote.totalValue, quote.currencyId, quote.currencyFormat, isTabularView]
  );

  const expirationInfo = getQuoteExpirationInfo(quote.expiresOn, theme);

  const handleAddClick = () => {
    if (quote.quoteId) {
      onAddToCart(quote.quoteId);
    }
  };

  const quoteHref = quote.quoteId
    ? AppendIfPrefixExists(`/quotes/${quote.quoteId}`)
    : undefined;

  const handleViewClick = () => {
    if (quoteHref) {
      navigate(quoteHref);
    }
  };

  return (
    <ListItem
      divider>
      <SkeletonWrapper loading={loading}>
        <ListItemText
          primary={
            quoteHref ? (
              <Link
                component={RouterLink}
                to={quoteHref}
                variant="body2"
                underline="hover"
                sx={[
                  {
                    display: "inline-block",
                    borderRadius: "8px",
                    padding: "4px 8px",
                    margin: "-4px -8px",
                    transition: "background-color 0.2s, opacity 0.2s",
                  },
                  (theme) =>
                    theme.applyStyles("light", {
                      "&:focus-visible": {
                        outline: "none",
                        backgroundColor: alpha(theme.palette.primary.dark, theme.palette.action.focusOpacity),
                      },
                    }),
                  (theme) =>
                    theme.applyStyles("dark", {
                      "&:focus-visible": {
                        outline: "none",
                        backgroundColor: alpha(theme.palette.primary.light, theme.palette.action.focusOpacity),
                      },
                    }),
                ]}
              >
                {quote.description}
              </Link>
            ) : (
              <Typography variant="body2">{quote.description}</Typography>
            )
          }
          slotProps={{
            secondary: { component: "div" },
          }}
          secondary={
            <Box sx={{ display: 'flex', gap: 1 }}>
              {secondaryTextParts.map((part, index) => (
                <Typography key={index} variant="caption" color="text.secondary">
                  {part}
                </Typography>
              ))}
            </Box>
          }
        />
      </SkeletonWrapper>
      {expirationInfo && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: expirationInfo.color,
              whiteSpace: 'nowrap',
            }}
          >
            {expirationInfo.text}
          </Typography>

          {expirationInfo.isExpired ? (
            <Button
              variant="text"
              size="small"
              sx={{ minWidth: '80px' }}
              onClick={handleViewClick}
            >
              View
            </Button>
          ) : (
            <Button
              variant="text"
              size="small"
              startIcon={isLoading ? <CircularProgress size={16} /> : <ShoppingCart />}
              sx={{ minWidth: '80px' }}
              onClick={handleAddClick}
              disabled={isLoading}
            >
              Add
            </Button>
          )}
        </Box>
      )}
    </ListItem>
  );
}

const shiComBaseUrl = getShiComBaseUrl();
const contactAccountTeamUrl = `${shiComBaseUrl}/CustomerService/contact-us`;

export default function RecentQuotesWidget({
  pageLayoutWidget,
}: {
  pageLayoutWidget: PageLayoutWidgetDto;
}) {
  const { getClaimValue, hasPermission } = useClaims();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);

  const orgUnitIdString = getClaimValue("orgUnitIdCurrent");
  const orgUnitId = orgUnitIdString ? parseInt(orgUnitIdString) : undefined;

  const {
    data: quotes,
    isError,
    isPlaceholderData,
  } = useGetQuotes(orgUnitId);

  const { mutation, pendingQuoteIds, snackbar, closeSnackbar, resultDialogProps } =
    useAddQuoteToCart();

  const handleAddToCart = (quoteId: number) => {
    mutation.mutate(quoteId);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  // Check permissions
  const hasOrgUnitPermission = hasPermission(ShiOnePermission.quoteViewOrgUnit);

  const nonDeletedQuotes = useMemo(
    () => quotes?.filter((quote) => !quote.isDeleted),
    [quotes]
  );

  // Filter quotes based on tabs (memoized to prevent unnecessary re-computation)
  const filteredQuotes = useMemo(
    () => filterAndSortQuotes(nonDeletedQuotes, hasOrgUnitPermission, selectedTab),
    [nonDeletedQuotes, hasOrgUnitPermission, selectedTab]
  );

  const handleViewAllQuotes = () => {
    navigate(AppendIfPrefixExists("/quotes"));
  };

  const shouldShowTabs = hasOrgUnitPermission && nonDeletedQuotes && nonDeletedQuotes.length > 0;

  return (
    <>
      <WidgetWrapper
        pageLayoutWidget={pageLayoutWidget}
        error={isError}
        noPadding
        cardHeaderDivider
        cardActions={
          nonDeletedQuotes && nonDeletedQuotes.length > 0 ? (
            <Button variant="text" onClick={handleViewAllQuotes}>
              View All Quotes
            </Button>
          ) : undefined
        }
        errorContactAccountTeamLink={contactAccountTeamUrl}
        hideWidgetContentOnError={true}
      >
        {/* Tabs for org-unit permission holders */}
        {shouldShowTabs && (
          <Tabs value={selectedTab} onChange={handleTabChange}>
            <Tab label="All Quotes" />
            <Tab label="My Quotes" />
            <Tab label="Storefront Quotes" />
          </Tabs>
        )}

        <List sx={{ padding: 0 }}>
          {filteredQuotes && filteredQuotes.length > 0 ? (
            filteredQuotes.map((quote) => (
              <QuoteItem
                key={quote.quoteId}
                quote={quote}
                loading={isPlaceholderData}
                onAddToCart={handleAddToCart}
                isTabularView={hasOrgUnitPermission}
                isLoading={
                  quote.quoteId ? pendingQuoteIds.has(quote.quoteId) : false
                }
              />
            ))
          ) : (
            <NoQuotesView />
          )}
        </List>
      </WidgetWrapper>

      <BuyAgainResultDialog {...resultDialogProps} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
